export type CallLog = {
  call_sid: string;
  date: string;
  caller: string;
  summary: string;
  duration_seconds?: number;
};

export function parseCallLogDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  let s = dateStr.trim().replace(/\s*UTC$/i, "Z"); // trailing " UTC" -> Z
  s = s.replace(" ", "T"); // first space -> T
  // if it has a time component but no zone designator, assume UTC
  if (s.includes("T") && !/[zZ]$/.test(s) && !/[+\-]\d{2}:?\d{2}$/.test(s)) s += "Z";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function parseCallLogs(data: unknown): CallLog[] {
  const list: unknown[] = Array.isArray(data)
    ? data
    : Array.isArray((data as { calls?: unknown })?.calls)
    ? (data as { calls: unknown[] }).calls
    : Array.isArray((data as { call_logs?: unknown })?.call_logs)
    ? (data as { call_logs: unknown[] }).call_logs
    : Array.isArray((data as { data?: unknown })?.data)
    ? (data as { data: unknown[] }).data
    : [];

  return list.map((item) => {
    const r = (typeof item === "object" && item !== null ? item : {}) as Record<string, unknown>;
    return {
      call_sid: String(r.call_sid ?? r.id ?? r.callSid ?? ""),
      date: String(r.date ?? r.created_at ?? r.timestamp ?? r.call_date ?? ""),
      caller: String(
        r.caller ??
          r.from ??
          r.phone ??
          r.caller_phone ??
          r.external_number ??
          "",
      ),
      summary: String(r.summary ?? r.summary_text ?? r.description ?? ""),
      duration_seconds: Number(r.duration_seconds ?? r.duration ?? 0),
    };
  });
}

export function parseCallLogTotal(data: unknown): number | null {
  if (typeof data !== "object" || data === null) return null;
  const total = (data as { total?: unknown }).total;
  if (typeof total === "number" && Number.isFinite(total) && total >= 0) return total;
  return null;
}

export type CallTurn = {
  sequence: number;
  role: "user" | "assistant" | "system";
  text: string;
  occurred_at: string | null;
  source: string;
};

export type CallDetail = {
  call_sid: string;
  status: string;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  turns: CallTurn[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function asString(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function asNullableString(value: unknown): string | null {
  if (value == null) return null;
  const s = asString(value);
  return s === "" && typeof value !== "string" ? null : s;
}

function asNullableNumber(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function turnRole(value: unknown): CallTurn["role"] {
  if (value === "user" || value === "system") return value;
  return "assistant";
}

export function parseCallDetail(data: unknown): CallDetail {
  const r = asRecord(data);
  const rawTurns = r && Array.isArray(r.turns) ? r.turns : [];
  const turns: CallTurn[] = [];

  for (const item of rawTurns) {
    const t = asRecord(item);
    if (!t) continue;
    const text = asString(t.text).trim();
    if (!text) continue;
    const sequence = asNullableNumber(t.sequence);
    turns.push({
      sequence: sequence ?? 0,
      role: turnRole(t.role),
      text,
      occurred_at: asNullableString(t.occurred_at),
      source: asString(t.source),
    });
  }

  turns.sort((a, b) => a.sequence - b.sequence);

  return {
    call_sid: r ? asString(r.call_sid) : "",
    status: r ? asString(r.status) : "",
    started_at: r ? asNullableString(r.started_at) : null,
    ended_at: r ? asNullableString(r.ended_at) : null,
    duration_seconds: r ? asNullableNumber(r.duration_seconds) : null,
    turns,
  };
}
